import { useState, useEffect, useRef } from 'react'
import { getApiClient } from '@/services/api'
import type { GroundingImageOption } from '@/services/api'
import { trackApiError } from '@/services/telemetry'

export interface UseGroundingImagesPreviewResult {
  images: GroundingImageOption[]
  tier: 1 | 2 | 3
  isLoading: boolean
  error: string | null
}

/**
 * Fetch grounding images for the landing screen pebbles without a session.
 *
 * Calls the preview endpoint so location images can appear in decorative
 * pebble shapes before the user creates a session. When no location is
 * provided, the backend falls back to the `default_grounding_location` setting.
 *
 * @param enabled  - Set to false once a session is created to stop fetching;
 *                   any in-flight request is aborted but last state is retained.
 * @param location - Optional location override; backend uses its default if omitted.
 */
export function useGroundingImagesPreview(
  enabled: boolean = true,
  location?: string
): UseGroundingImagesPreviewResult {
  const [images, setImages] = useState<GroundingImageOption[]>([])
  const [tier, setTier] = useState<1 | 2 | 3>(3)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!enabled) return

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsLoading(true)
    setError(null)

    const client = getApiClient()
    client
      .getGroundingImagesPreview(location, undefined, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return
        setImages(Array.isArray(result.images) ? result.images : [])
        setTier(result.tier ?? 3)
        if (result.error) setError(result.error)
      })
      .catch((err) => {
        if (controller.signal.aborted) return
        const statusCode = (err as { response?: { status?: number } })?.response?.status
        void trackApiError(
          err instanceof Error ? err : new Error(String(err)),
          '/api/v1/grounding-images/preview',
          undefined,
          statusCode,
        )
        setError('Could not load location images')
        setImages([])
        setTier(3)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })

    return () => {
      controller.abort()
    }
  }, [enabled, location])

  return { images, tier, isLoading, error }
}
